import type { NormalizedCacheObject } from "@apollo/client";
import { ApolloClient, ApolloLink } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { useMemo } from "react";

import { unauthorizedErrorRedirect } from "@helpers/unauthorizedErrorRedirect/unauthorizedErrorRedirect";
import { getAddEventIdLink } from "@shared/data/client/links/getAddEventIdLink/getAddEventIdLink";
import { getAddUTMLink } from "@shared/data/client/links/getAddUTMLink/getAddUTMLink";
import { getFrontendMetaDataLink } from "@shared/data/client/links/getFrontendMetaDataLink/getFrontendMetaDataLink";
import { getPersistedQueriesLink } from "@shared/data/client/links/getPersistedQueriesLink/getPersistedQueriesLink";
import { getShouldFrontendRefreshLink } from "@shared/data/client/links/getShouldFrontendRefreshLink/getShouldFrontendRefreshLink";
import { getUploadLink } from "@shared/data/client/links/uploadLink";
import { initializeEventIds } from "@shared/helpers/eventIdManager";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { isProduction } from "@shared/utils/fullscriptEnv/fullscriptEnv";
import { turnOffErrorReportingIfLoggedOut } from "@shared/utils/initSentry/filters/turnErrorsOff/turnErrorsOff";

import { getCache } from "./cache";

const useClient = (): ApolloClient<NormalizedCacheObject> => {
  const [gqlPersistedQueriesFlipper] = useFlippers("gql_persisted_queries");

  const getClient = () => {
    const getLink = () => {
      const uploadLink = getUploadLink();
      initializeEventIds();

      const links = [
        onError(({ networkError, response }) => {
          turnOffErrorReportingIfLoggedOut(networkError, response);
          unauthorizedErrorRedirect(networkError);
        }),
        getFrontendMetaDataLink(),
        getAddUTMLink(),
        getAddEventIdLink(),
        getShouldFrontendRefreshLink(),
      ];

      if (gqlPersistedQueriesFlipper) {
        const persistedQueriesLink = getPersistedQueriesLink();
        links.push(persistedQueriesLink);
      }

      return ApolloLink.from([...links, uploadLink]);
    };

    return new ApolloClient({
      link: getLink(),
      cache: getCache(),
      connectToDevTools: !isProduction(),
    });
  };

  return useMemo(() => getClient(), [gqlPersistedQueriesFlipper]);
};

export { useClient };
